import type { MockOrder, OrderStatus } from '../../../mocks/orders';

interface Props {
  order: MockOrder;
}

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; icon: string; bg: string }> = {
  pending:   { label: 'بانتظار التأكيد', color: '#C9A961', icon: 'ri-time-line',         bg: '#C9A96115' },
  processing:{ label: 'قيد التجهيز',     color: '#F59E0B', icon: 'ri-settings-3-line',   bg: '#F59E0B15' },
  shipped:   { label: 'في الطريق إليك',  color: '#3B82F6', icon: 'ri-truck-line',         bg: '#3B82F615' },
  delivered: { label: 'تم التوصيل',      color: '#10B981', icon: 'ri-checkbox-circle-line',bg: '#10B98115' },
  cancelled: { label: 'ملغي',            color: '#EF4444', icon: 'ri-close-circle-line',  bg: '#EF444415' },
};

export default function TrackingResult({ order }: Props) {
  const cfg = STATUS_CONFIG[order.status];

  return (
    <div className="space-y-5 animate-[fadeIn_0.4s_ease]" dir="rtl">

      {/* Status banner */}
      <div
        className="rounded-2xl p-5 flex items-center gap-4"
        style={{ background: cfg.bg, border: `1.5px solid ${cfg.color}30` }}
      >
        <div
          className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0"
          style={{ background: cfg.color + '20' }}
        >
          <i className={`${cfg.icon} text-2xl`} style={{ color: cfg.color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-gray-500">رقم الطلب</span>
            <span className="text-xs font-black text-gray-800" dir="ltr">#{order.id}</span>
            <span
              className="text-xs font-black px-2.5 py-0.5 rounded-full"
              style={{ background: cfg.color + '20', color: cfg.color }}
            >
              {cfg.label}
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-1">تاريخ الطلب: {order.createdAt}</p>
        </div>
        <div className="text-left flex-shrink-0">
          <p className="text-xs text-gray-400">الإجمالي</p>
          <p className="text-lg font-black text-gray-900">{order.total} <span className="text-sm font-bold">د.إ</span></p>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl p-5">
        <h3 className="text-sm font-black text-gray-800 mb-5 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-route-line text-[#C9A961]" />
          </div>
          مسار الطلب
        </h3>
        <div className="space-y-0">
          {order.timeline.map((step, i) => {
            const isLast = i === order.timeline.length - 1;
            const stepCfg = STATUS_CONFIG[step.status];
            return (
              <div key={i} className="flex gap-4">
                {/* Left: icon + connector */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all"
                    style={
                      step.done
                        ? { background: stepCfg.color, color: '#fff' }
                        : { background: '#F3F4F6', color: '#D1D5DB' }
                    }
                  >
                    {step.done
                      ? <i className="ri-check-line text-base" />
                      : <i className={`${stepCfg.icon} text-base`} />
                    }
                  </div>
                  {!isLast && (
                    <div
                      className="w-0.5 flex-1 min-h-[32px] my-1 rounded-full"
                      style={{ background: step.done ? stepCfg.color + '50' : '#E5E7EB' }}
                    />
                  )}
                </div>

                {/* Right: content */}
                <div className={`pb-5 flex-1 ${isLast ? '' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`text-sm font-black ${step.done ? 'text-gray-900' : 'text-gray-300'}`}>
                        {step.label}
                      </p>
                      <p className={`text-xs mt-0.5 ${step.done ? 'text-gray-500' : 'text-gray-300'}`}>
                        {step.desc}
                      </p>
                    </div>
                    <span className={`text-[10px] font-bold whitespace-nowrap flex-shrink-0 ${step.done ? 'text-gray-400' : 'text-gray-200'}`}>
                      {step.date}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order items */}
      <div className="bg-white rounded-2xl p-5">
        <h3 className="text-sm font-black text-gray-800 mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-shopping-bag-line text-[#C9A961]" />
          </div>
          المنتجات ({order.items.length})
        </h3>
        <div className="space-y-3">
          {order.items.map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-[#FAFAF8] rounded-xl">
              <img
                src={item.image}
                alt={item.name}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">{item.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">الكمية: {item.qty}</p>
              </div>
              <p className="text-sm font-black text-[#C9A961] flex-shrink-0">{item.price * item.qty} د.إ</p>
            </div>
          ))}
        </div>

        {/* Price breakdown */}
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          <div className="flex justify-between text-sm text-gray-500">
            <span>المجموع الفرعي</span>
            <span>{order.subtotal} د.إ</span>
          </div>
          <div className="flex justify-between text-sm text-gray-500">
            <span>الشحن</span>
            <span>{order.shipping === 0 ? <span className="text-green-600 font-bold">مجاني</span> : `${order.shipping} د.إ`}</span>
          </div>
          <div className="flex justify-between text-base font-black text-gray-900 pt-2 border-t border-gray-100">
            <span>الإجمالي</span>
            <span className="text-[#C9A961]">{order.total} د.إ</span>
          </div>
        </div>
      </div>

      {/* Customer + Payment info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5">
          <h3 className="text-sm font-black text-gray-800 mb-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-[#C9A961]" />
            </div>
            عنوان التوصيل
          </h3>
          <p className="text-sm font-bold text-gray-800">{order.customer.name}</p>
          <p className="text-xs text-gray-500 mt-1">{order.customer.address}</p>
          <p className="text-xs text-gray-500">{order.customer.city}، الإمارات</p>
          <p className="text-xs text-gray-500 mt-1" dir="ltr">{order.customer.phone}</p>
        </div>
        <div className="bg-white rounded-2xl p-5">
          <h3 className="text-sm font-black text-gray-800 mb-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-bank-card-line text-[#C9A961]" />
            </div>
            طريقة الدفع
          </h3>
          <p className="text-sm font-bold text-gray-700">{order.payMethod}</p>
          <div className="mt-3 flex items-center gap-1.5 text-xs text-green-600">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line" />
            </div>
            معاملة آمنة ومحمية
          </div>
        </div>
      </div>

    </div>
  );
}
